// Copyright (c) 2025 WiseTime. All rights reserved.

import { EMPTY, from, Observable } from "rxjs"
import { Action } from "redux"
import { filter, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Slice from "../slices/timeline"
import * as UserContextSlice from "../../user-context/slice"
import * as Actions from "../actions"
import { RootState } from "../../../app/store"
import { isDisabledTimeGroup } from "../timeGroups"
import { SelectTimesheetFieldOptionRequest } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

/**
 * Check if all config hashes for a time group have their config data available in cache
 */
const areAllConfigsAvailable = (
  timeGroupId: string,
  timeGroupFieldConfigsCache: Slice.TimeGroupFieldConfigsCache,
  timesheetFieldConfigsCache: Slice.TimesheetFieldConfigsCache
): boolean => {
  const configHashes = timeGroupFieldConfigsCache[timeGroupId]
  if (!configHashes || configHashes.length === 0) {
    return false
  }
  return configHashes.every(hash => timesheetFieldConfigsCache[hash] !== undefined)
}

/**
 * Process awaiting time groups and apply suggestions for those with complete configs
 */
const processAwaitingTimeGroups = (state: RootState): Action[] => {
  const currentSiteId = UserContextSlice.selectCurrentSiteId(state)
  if (!currentSiteId) {
    return []
  }

  const timeGroupsAwaitingSuggestions = Slice.selectTimeGroupsAwaitingSuggestions(state)
  const timeGroupsCache = Slice.selectTimeGroupsCache(state)
  const timeGroupFieldConfigsCache = Slice.selectTimeGroupFieldConfigsCache(state)
  const timesheetFieldConfigsCache = Slice.selectTimesheetFieldConfigsCache(state)

  // Filter to time groups that are ready to process
  const readyTimeGroupIds = timeGroupsAwaitingSuggestions.filter(timeGroupId => {
    const timeGroup = timeGroupsCache[timeGroupId]
    return timeGroup
      && !isDisabledTimeGroup(timeGroup.postStatus)
      && areAllConfigsAvailable(timeGroupId, timeGroupFieldConfigsCache, timesheetFieldConfigsCache)
  })

  return readyTimeGroupIds.flatMap(timeGroupId => {
    const configuredTimesheetFields = Slice.selectConfiguredTimesheetFieldsCombiner(
      timeGroupId,
      currentSiteId,
      state.timeline
    )

    // Find fields that need default suggestions applied
    const fieldsNeedingSuggestions = configuredTimesheetFields.filter(field =>
      field.timesheetFieldConfig.suggestedTimesheetFieldOptionId &&
      !field.selectedTimesheetFieldOption &&
      field.timesheetFieldConfig.optionsList.length > 0
    )

    // Select actions for fields needing suggestions
    const selectActions = fieldsNeedingSuggestions.map(field =>
      Actions.selectTimesheetFieldOption(
        new SelectTimesheetFieldOptionRequest()
          .setTimeGroupId(timeGroupId)
          .setTimesheetFieldId(field.timesheetField.id)
          .setSiteId(currentSiteId)
          .setTimesheetFieldOptionId(field.timesheetFieldConfig.suggestedTimesheetFieldOptionId ?? "")
      )
    )

    // Remove from awaiting list (always remove, even if no suggestions needed)
    return [...selectActions, Slice.removeTimeGroupAwaitingSuggestions(timeGroupId)]
  })
}

export const applySuggestedTimesheetFieldOptions = () =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(action =>
        Slice.setTimeGroupFieldConfigs.match(action) || Slice.setTimesheetFieldConfigs.match(action)),
      withLatestFrom(state$),
      mergeMap(([_, state]) => {
        const actions = processAwaitingTimeGroups(state)

        if (actions.length === 0) {
          return EMPTY
        }

        return from(actions)
      }),
    )

export default applySuggestedTimesheetFieldOptions()
